'use client';

/**
 * GoogleReviewsHero — V3 hero for /google-reviews.
 *
 * Frames the invisible problem before naming the product (per V3 copy
 * pattern): bad reviews damage rating before the owner sees them. The
 * page argues that SUM intercepts feedback at the loyalty card and
 * routes it intelligently — green to Google, red/orange straight to
 * the owner's inbox.
 *
 * Cyan accent (brand primary) — Google Reviews is core retention, not
 * a "feature page rotation".
 */

import Image from 'next/image';
import React from 'react';
import { Locale } from '../../../../i18n-config';
import { useTranslation } from '@/i18n/client';
import FeatureHero from '../_shared/sections/FeatureHero';

type Props = { lang: Locale };

const SMART_FILTER_IMAGE = 'https://cdn.1cdn.app/application/UMD/26050207384898_smart_google_reviews.png';

const GoogleReviewsHero = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const visual = (
    <Image
      src={SMART_FILTER_IMAGE}
      alt={t('GoogleReviews/SmartRouting/image_alt')}
      width={840}
      height={840}
      sizes="(min-width: 1024px) 320px, 70vw"
      className="mx-auto h-auto w-3/4 max-w-[320px] rounded-3xl lg:w-full"
      priority
    />
  );

  return (
    <FeatureHero
      accent="#22D3EE"
      ariaLabel={t('GoogleReviews/Hero/aria_label')}
      copy={{
        kicker: t('GoogleReviews/Hero/badge'),
        headlineA: t('GoogleReviews/Hero/title1'),
        headlineB: t('GoogleReviews/Hero/title2'),
        subhead: t('GoogleReviews/Hero/subtitle'),
        ctaPrimary: {
          label: t('GoogleReviews/Hero/cta_primary'),
          href: `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL}/register`,
        },
        ctaSecondary: {
          label: t('GoogleReviews/Hero/cta_secondary'),
          href: '#how-it-works',
        },
      }}
      visual={visual}
      visualVisibleOnMobile
    />
  );
};

export default GoogleReviewsHero;
